'use client'; // Needed for hooks like usePathname

import { NetworkLink } from '@/components/ui/NetworkLink';
import { SearchInput } from '@/components/ui/SearchInput';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { SignInButton, SignedIn, SignedOut, UserButton } from '@clerk/nextjs';
import { LogInIcon, Menu, XIcon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { SparkScanLogo } from '../ui/icons';
import { NetworkSwitcher } from './NetworkSwitcher';

export function Header() {
  const pathname = usePathname();
  // const isExploreActive = pathname.startsWith("/explore");
  // const _isStatsActive = pathname.startsWith("/stats");
  // const _isChartsActive = pathname.startsWith("/charts");
  const isHomePage = pathname === '/';

  // Cycle through placeholders
  const placeholders = useMemo(
    () => [
      'Search for anything...',
      'Search by Account ID...',
      'Search by Token ID...',
      'Search by Transaction...',
    ],
    []
  );
  const [placeholder, setPlaceholder] = useState(placeholders[0]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setPlaceholder((current) => {
        const currentIndex = placeholders.indexOf(current);
        const nextIndex = (currentIndex + 1) % placeholders.length;
        return placeholders[nextIndex];
      });
    }, 3000); // Change every 3 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [placeholders]);

  const handleSearch = (query: string) => {
    // The SearchInput component now handles the navigation directly,
    // but we can still add additional logging or analytics here
    console.log('Search from header:', query);
  };

  const NavItems = () => (
    <>
      {/*
      <NetworkLink
        href="/explore"
        className={cn(
          "transition-colors hover:text-foreground/80",
          isExploreActive ? "text-foreground" : "text-foreground/60"
        )}>
        Explore
      </NetworkLink>
       <NetworkLink
        href="/stats" // Link to stats page (create later)
        className={cn(
          "transition-colors hover:text-foreground/80",
          isStatsActive ? "text-foreground" : "text-foreground/60"
        )}>
        Stats
      </NetworkLink>
      <NetworkLink
        href="/charts" // Link to charts page
        className={cn(
          "transition-colors hover:text-foreground/80",
          isChartsActive ? "text-foreground" : "text-foreground/60"
        )}>
        Charts
      </NetworkLink> */}
    </>
  );

  const NavItemsMobile = () => (
    <>
      {/*
       <SheetClose asChild>
         <NetworkLink
           href="/explore"
           className={cn(
             "transition-colors hover:text-foreground/80 text-2xl font-medium py-2 flex items-center",
             "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md px-2",
             isExploreActive ? "text-foreground" : "text-foreground/60"
           )}>
           Explore
         </NetworkLink>
       </SheetClose>
       <SheetClose asChild>
        <NetworkLink
          href="/stats"
          className={cn(
            "transition-colors hover:text-foreground/80 text-2xl font-medium py-2 flex items-center",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md px-2",
            isStatsActive ? "text-foreground" : "text-foreground/60"
          )}>
          Stats
        </NetworkLink>
      </SheetClose>
      <SheetClose asChild>
        <NetworkLink
          href="/charts" // Link to charts page
          className={cn(
            "transition-colors hover:text-foreground/80 text-2xl font-medium py-2 flex items-center",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md px-2",
            isChartsActive ? "text-foreground" : "text-foreground/60"
          )}>
          Charts
        </NetworkLink>
      </SheetClose> */}
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="relative h-14 max-w-screen-xl mx-auto" suppressHydrationWarning>
        <div className="flex h-full items-center justify-between px-4 sm:px-6 w-full">
          <div className="flex items-center">
            <NetworkLink href="/" className="flex items-center space-x-2">
              <SparkScanLogo className="h-4 w-4" />
              <span className="font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
                Sparkscan
              </span>
            </NetworkLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center ml-6 space-x-6 text-sm font-medium">
              <NavItems />
            </nav>
          </div>

          <div className="flex items-center space-x-3">
            {/* Network Switcher - Desktop */}
            <div className="hidden md:flex">
              <NetworkSwitcher />
            </div>
            <div className="hidden md:flex">
              <SignedOut>
                <SignInButton>
                  <Button variant="outline" size="sm" className="gap-1 cursor-pointer">
                    <LogInIcon className="h-4 w-4" />
                  </Button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <UserButton />
              </SignedIn>
            </div>

            {/* Mobile Navigation Toggle - now on the right */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" aria-label="Menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="top"
                className="h-[100dvh] w-full overflow-y-auto p-6"
                hideCloseButton
              >
                <div className="flex justify-between items-center mb-10">
                  <div className="flex items-center">
                    <SheetClose asChild>
                      <NetworkLink href="/" className="flex items-center gap-2">
                        <SparkScanLogo className="h-4 w-4" />
                        <span className="font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
                          Sparkscan
                        </span>
                      </NetworkLink>
                    </SheetClose>
                  </div>
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 p-0"
                      aria-label="Close menu"
                    >
                      <XIcon className="h-7 w-7" />
                      <span className="sr-only">Close</span>
                    </Button>
                  </SheetClose>
                </div>
                <div className="flex flex-col h-[calc(100%-5rem)]">
                  <div className="flex flex-col gap-10">
                    {!isHomePage && (
                      <div className="w-full">
                        <SearchInput placeholder="Search..." onSearch={handleSearch} />
                      </div>
                    )}
                    <nav className="flex flex-col space-y-8">
                      <NavItemsMobile />
                    </nav>

                    <div className="mt-auto pt-10 flex items-center space-x-4">
                      {/* Network Switcher - Mobile */}
                      <NetworkSwitcher />
                    </div>

                    <div className="mt-auto pt-10 flex items-center space-x-4">
                      {/* Login/Signup - Mobile */}
                      <SignedOut>
                        <SignInButton>
                          <Button variant="outline" size="sm" className="gap-1 cursor-pointer">
                            <LogInIcon className="h-4 w-4" />
                          </Button>
                        </SignInButton>
                      </SignedOut>
                      <SignedIn>
                        <UserButton />
                      </SignedIn>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {!isHomePage && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md px-4 hidden sm:block">
            <SearchInput placeholder={placeholder} variant="header" onSearch={handleSearch} />
          </div>
        )}
      </div>
    </header>
  );
}
